/**
 * Product-InlineAccordions.js
 *
 * Controla los accordions inline de la PDP.
 *
 * Reglas:
 * - Solo puede permanecer un item abierto.
 * - Al abrir uno, se cierra el anterior.
 * - El componente funciona también después de reemplazos AJAX
 *   porque se implementa como Custom Element.
 */
(() => {
  "use strict";

  class ProductInlineAccordions extends HTMLElement {
    constructor() {
      super();

      this.handleClick =
        this.handleClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener(
        "click",
        this.handleClick
      );

      this.normalizeInitialState();
    }

    disconnectedCallback() {
      this.removeEventListener(
        "click",
        this.handleClick
      );
    }

    get items() {
      return Array.from(
        this.querySelectorAll(
          "[data-product-inline-accordion-item]"
        )
      );
    }

    handleClick(event) {
      const trigger = event.target.closest(
        "[data-product-inline-accordion-trigger]"
      );

      if (
        !trigger ||
        !this.contains(trigger)
      ) {
        return;
      }

      event.preventDefault();

      const item = trigger.closest(
        "[data-product-inline-accordion-item]"
      );

      if (!item) {
        return;
      }

      const shouldOpen =
        !item.classList.contains("is-open");

      /*
       * Siempre cerramos los demás.
       */
      this.closeAll(item);

      if (shouldOpen) {
        this.openItem(item);
      } else {
        this.closeItem(item);
      }
    }

    normalizeInitialState() {
      const initiallyOpenItems =
        this.items.filter((item) =>
          item.classList.contains("is-open")
        );

      /*
       * Si por configuración más de uno viene abierto,
       * conservamos únicamente el primero.
       */
      initiallyOpenItems
        .slice(1)
        .forEach((item) => {
          this.closeItem(item);
        });

      this.items.forEach((item) => {
        const isOpen =
          item.classList.contains("is-open");

        this.setAccessibilityState(
          item,
          isOpen
        );
      });
    }

    openItem(item) {
      item.classList.add("is-open");

      this.setAccessibilityState(
        item,
        true
      );

      this.dispatchEvent(
        new CustomEvent(
          "product:inline-accordion:open",
          {
            bubbles: true,
            detail: {
              item,
            },
          }
        )
      );
    }

    closeItem(item) {
      item.classList.remove("is-open");

      this.setAccessibilityState(
        item,
        false
      );
    }

    closeAll(exceptItem = null) {
      this.items.forEach((item) => {
        if (item !== exceptItem) {
          this.closeItem(item);
        }
      });
    }

    setAccessibilityState(
      item,
      isOpen
    ) {
      const trigger = item.querySelector(
        "[data-product-inline-accordion-trigger]"
      );

      const panel = item.querySelector(
        "[data-product-inline-accordion-panel]"
      );

      trigger?.setAttribute(
        "aria-expanded",
        String(isOpen)
      );

      panel?.setAttribute(
        "aria-hidden",
        String(!isOpen)
      );
    }
  }

  if (
    !customElements.get(
      "product-inline-accordions"
    )
  ) {
    customElements.define(
      "product-inline-accordions",
      ProductInlineAccordions
    );
  }
})();